import { ref } from "vue";
import { useStore } from "@/core/services/store";
import { UPDATE_SELECTED_PROJECT } from "@/core/services/store/switchArea.module";
import { useMigrationIntegration } from "./useMigrationIntegration";
import i18n from "@/core/plugins/vue-i18n";
import _ from "lodash";
import useOauth from "@/components/ExternalApps/SalesChannelManagementApp/composables/useOauth";

export function useIntegrationStatus() {
  const store = useStore();
  const { utf8ToBase64 } = useOauth();
  const {
    rawChannels,
    migrationErrorParams,
    selectedChannelId,
    createdProject,
    error,
    show,
    step
  } = useMigrationIntegration();

  const statusDialog = ref(false);
  const selectedIntegrationForStatus = ref(null);

  const statusResolver = (integration, licenseId) => {
    const id = integration?.id || "unknown";
    return `${id}-${licenseId}`;
  };

  const calcStatus = _.memoize((integration, licenseId, appName) => {
    const showVersioningWarning = appName === "otto";

    if (!showVersioningWarning) {
      return "normal";
    }

    if (
      integration?.salesChannel?.value?.migrationError?.message ||
      integration?.salesChannel?.value?.migrationError
    ) {
      return "critical";
    }

    if (integration?.status && integration?.status !== "UP_TO_DATE") {
      if (integration.status === "ACTION_REQUIRED") {
        return "critical";
      } else if (integration.status === "EXPIRES_SOON") {
        return "warning";
      }
    }

    return "normal";
  }, statusResolver);

  /**
   * Determines the severity level of an integration's status
   * @param {Object} integration - The integration object
   * @returns {string} - 'critical', 'warning', or 'normal'
   */
  function getStatusSeverity(integration) {
    // We pull the state here so we can pass it as arguments
    const licenseId = store.getters.licenseId;
    const appName = store.getters.selectedApp.name;

    return calcStatus(integration, licenseId, appName);
  }

  /**
   * Gets CSS class names based on status severity
   * @param {string} severity - The severity level
   * @returns {Object} - Object containing CSS class names for styling
   */
  function getStatusColor(severity) {
    switch (severity) {
      case "critical":
        return {
          containerClass: "integration-status--critical",
          textClass: "status-text--critical",
          borderClass: "integration-status--critical-border",
          backgroundClass: "integration-status--critical-bg"
        };
      case "warning":
        return {
          containerClass: "integration-status--warning",
          textClass: "status-text--warning",
          borderClass: "integration-status--warning-border",
          backgroundClass: "integration-status--warning-bg"
        };
      default:
        return {
          containerClass: "integration-status--normal",
          textClass: "status-text--normal",
          borderClass: "integration-status--normal-border",
          backgroundClass: "integration-status--normal-bg"
        };
    }
  }

  /**
   * Gets the status message for an integration
   * @param {Object} integration - The integration object
   * @returns {string} - The status message
   */
  function getStatusMessage(integration) {
    // Use integration status if available
    if (integration.status && integration.status !== "UP_TO_DATE") {
      if (integration.status === "EXPIRES_SOON") {
        return i18n.t(
          "salesChannelManagementApp.integrationStatus.authorizationExpiresTitle"
        );
      } else if (integration.status === "ACTION_REQUIRED") {
        return i18n.t(
          "salesChannelManagementApp.integrationStatus.actionRequiredTitle"
        );
      }
    }

    return "";
  }

  /**
   * Gets the action message for an integration status
   * @param {Object} integration - The integration object
   * @returns {string} - The action message
   */
  function getStatusActionMessage(integration) {
    const severity = getStatusSeverity(integration);
    const { status } = integration;

    const messages = {
      critical: {
        ACTION_REQUIRED:
          "salesChannelManagementApp.integrationStatus.actionRequiredText"
      },
      warning: {
        EXPIRES_SOON:
          "salesChannelManagementApp.integrationStatus.authorizationExpiresText"
      }
    };

    const key = messages[severity]?.[status];
    return key ? i18n.t(key) : "";
  }

  /**
   * Determines if a warning icon should be shown for an integration
   * @param {Object} integration - The integration object
   * @returns {boolean} - Whether to show warning icon
   */
  function shouldShowWarningIcon(integration) {
    return getStatusSeverity(integration) !== "normal";
  }

  /**
   * Opens the status dialog for a specific integration
   * @param {Object} integration - The integration object
   */
  function openStatusDialog(integration) {
    selectedIntegrationForStatus.value = integration;
    statusDialog.value = true;
  }

  /**
   * Closes the status dialog and resets selected integration
   */
  function closeStatusDialog() {
    statusDialog.value = false;
    selectedIntegrationForStatus.value = null;
  }

  /**
   * Handles reauthorization for an integration
   * @param {Object} integration - The integration object
   */
  function handleReauthorize(integration) {
    const config = store.getters.xentralConfig?.data ?? {};

    const baseUrl = (config.baseUrl || "").replace(/\/$/, "");
    const endpoint = config.xentralAppUri || "";
    const target = `${baseUrl}/${endpoint}`;

    const coreDomain = process.env.VUE_APP_CORE_DOMAIN;

    const url =
      `https://oauth.api.${coreDomain}/apps/initializeReauthorization/${integration.id}` +
      `?from_url=${encodeURIComponent(utf8ToBase64(target))}`;

    window.open(url);
  }

  /**
   * Handles opening error details for an integration
   * @param {Object} integration - The integration object
   */
  function onOpenError(integration) {
    if (
      !integration?.salesChannel?.value?.migrationError?.message &&
      !integration?.salesChannel?.value?.migrationError
    )
      return;

    store.dispatch(UPDATE_SELECTED_PROJECT, integration);

    migrationErrorParams.value =
      integration.salesChannel?.value?.migrationError?.params;
    selectedChannelId.value = {
      ...selectedChannelId,
      channelId:
        migrationErrorParams.value?.uuid ?? migrationErrorParams.value?.id
    };

    createdProject.value = integration;
    error.value =
      integration.salesChannel?.value?.migrationError?.message ??
      (typeof integration.salesChannel?.value?.migrationError === "string"
        ? integration.salesChannel?.value?.migrationError
        : "");
    show.value = true;
    step.value = 4;
  }

  /**
   * Gets channel information for an integration
   * @param {Object} integration - The integration object
   * @returns {Object|undefined} - The channel info object
   */
  function getChannelInfo(integration) {
    if (
      !integration?.salesChannel?.value?.id &&
      !integration?.salesChannel?.value?.uuid
    )
      return;

    return rawChannels.value.find(
      c =>
        c.id === integration?.salesChannel?.value?.id ||
        c.uuid === integration?.salesChannel?.value?.uuid
    );
  }

  return {
    // State
    statusDialog,
    selectedIntegrationForStatus,

    // Functions
    getStatusSeverity,
    getStatusColor,
    getStatusMessage,
    getStatusActionMessage,
    shouldShowWarningIcon,
    openStatusDialog,
    closeStatusDialog,
    handleReauthorize,
    onOpenError,
    getChannelInfo
  };
}
